/**
 * BackgroundTab — background type selector (solid / gradient / image) + effects.
 * Single responsibility: background-related style properties.
 */

import React, { useState } from 'react';
import type { StyleProperties } from '../types';
import { ColorPicker } from '../shared/ColorPicker';
import { SliderInput } from '../shared/SliderInput';
import { SegmentedControl } from '../shared/SegmentedControl';
import { SectionLabel } from '../shared/SectionLabel';

/**
 * HARDENING (2026-08-30, second independent code review — Low/Plausible, defense-in-depth): both
 * call sites below splice a raw picked/typed URL into `url('${url}')` with no escaping of an
 * embedded single quote — currently caught only incidentally, because a quote-breakout attempt
 * that also tries to inject a sibling declaration would still contain a `{`/`}`/`@` that this
 * codebase's `isUnsafeCssValue` guard (`app-handlers-generic`'s `cssInjector.ts`) rejects
 * downstream. That's relying on a guard in a DIFFERENT package to cover a gap in this one — this
 * function makes the value safe on its own construction, not just safe because of what happens to
 * consume it later. Escapes backslash first (so the following quote-escape doesn't double-escape
 * a user-typed backslash), then the single quote itself.
 */
function escapeCssUrlLiteral(url: string): string {
  return url.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

export interface BackgroundTabProps {
  value: StyleProperties;
  onChange: (p: StyleProperties) => void;
  isDark: boolean;
  /** Opens an asset picker and resolves to the picked asset's own URL — optional (2026-08-30,
   *  wix-style-design Task 7). This component has zero knowledge of what backs the picker (a
   *  digital-assets-library, a different media source, or nothing at all); omitted entirely,
   *  the Image URL field behaves exactly as it always has — every existing Atlas Forms consumer
   *  with no such picker to offer stays byte-for-byte unaffected. */
  onBrowseAssets?: () => Promise<string>;
}

type BgMode = 'solid' | 'gradient' | 'image';

/** Safely parse a number from a filter function string, e.g. "brightness(1.2)" → 1.2 */
function parseFilterFn(filter: string | undefined, fn: string): number | undefined {
  if (!filter) return undefined;
  const re = new RegExp(`${fn}\\(([^)]+)\\)`);
  const m = filter.match(re);
  if (!m) return undefined;
  const raw = m[1].replace('%', '');
  const n = parseFloat(raw);
  return isNaN(n) ? undefined : n;
}

/** Compose a filter string from individual values. */
function composeFilter(brightness: number | undefined, grayscale: number | undefined): string | undefined {
  const parts: string[] = [];
  if (brightness !== undefined && brightness !== 100) {
    parts.push(`brightness(${(brightness / 100).toFixed(2)})`);
  }
  if (grayscale !== undefined && grayscale !== 0) {
    parts.push(`grayscale(${grayscale}%)`);
  }
  return parts.length > 0 ? parts.join(' ') : undefined;
}

/** Detect gradient in backgroundImage, extract angle + from/to colors. */
function parseLinearGradient(img: string | undefined): { angle: number; from: string; to: string } | undefined {
  if (!img) return undefined;
  const m = img.match(/^linear-gradient\((\d+)deg,\s*([^,]+),\s*(.+)\)$/);
  if (!m) return undefined;
  return { angle: parseInt(m[1], 10), from: m[2].trim(), to: m[3].trim() };
}

const BG_SIZE_OPTIONS = [
  { value: 'cover', label: 'Cover' },
  { value: 'contain', label: 'Contain' },
  { value: 'auto', label: 'Auto' },
] as const;

const BG_POSITION_OPTIONS = [
  { value: 'center', label: 'Center' },
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
] as const;

const BG_REPEAT_OPTIONS = [
  { value: 'no-repeat', label: 'No-repeat' },
  { value: 'repeat', label: 'Repeat' },
  { value: 'repeat-x', label: 'Repeat-X' },
  { value: 'repeat-y', label: 'Repeat-Y' },
] as const;

const BG_MODE_OPTIONS = [
  { value: 'solid', label: 'Solid' },
  { value: 'gradient', label: 'Gradient' },
  { value: 'image', label: 'Image' },
] as const;

/**
 * BackgroundTab — solid/gradient/image background + filter effects.
 */
export const BackgroundTab: React.FC<BackgroundTabProps> = ({ value, onChange, isDark, onBrowseAssets }) => {
  // Determine initial mode from current value
  const inferMode = (): BgMode => {
    if (value.backgroundImage) {
      if (value.backgroundImage.startsWith('linear-gradient') || value.backgroundImage.startsWith('radial-gradient')) {
        return 'gradient';
      }
      return 'image';
    }
    return 'solid';
  };

  const [mode, setMode] = useState<BgMode>(inferMode);

  // Gradient state
  const parsedGrad = parseLinearGradient(value.backgroundImage);
  const [gradAngle, setGradAngle] = useState<number>(parsedGrad?.angle ?? 135);
  const [gradFrom, setGradFrom] = useState<string>(parsedGrad?.from ?? '#000000');
  const [gradTo, setGradTo] = useState<string>(parsedGrad?.to ?? '#ffffff');

  // Image URL state
  const [imageUrl, setImageUrl] = useState<string>(() => {
    if (value.backgroundImage && value.backgroundImage.startsWith('url(')) {
      return value.backgroundImage.replace(/^url\(['"]?/, '').replace(/['"]?\)$/, '');
    }
    return '';
  });

  // Filter state — derive from existing filter string
  const existingFilter = value.filter;
  const [brightness, setBrightness] = useState<number | undefined>(() => {
    const v = parseFilterFn(existingFilter, 'brightness');
    return v !== undefined ? Math.round(v * 100) : undefined;
  });
  const [grayscale, setGrayscale] = useState<number | undefined>(() =>
    parseFilterFn(existingFilter, 'grayscale')
  );

  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const inputBg = isDark ? 'rgba(2,6,23,0.6)' : '#f8fafc';
  const borderColor = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';

  const stackStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  const urlInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '5px 8px',
    borderRadius: 4,
    border: `1px solid ${borderColor}`,
    background: inputBg,
    color: textPrimary,
    fontSize: 12,
    boxSizing: 'border-box',
  };

  const applyGradient = (angle: number, from: string, to: string) => {
    onChange({
      ...value,
      backgroundColor: undefined,
      backgroundImage: `linear-gradient(${angle}deg, ${from}, ${to})`,
    });
  };

  const applyFilters = (b: number | undefined, g: number | undefined) => {
    onChange({ ...value, filter: composeFilter(b, g) });
  };

  const handleModeChange = (m: BgMode) => {
    setMode(m);
    if (m === 'solid') {
      onChange({ ...value, backgroundImage: undefined });
    } else if (m === 'gradient') {
      applyGradient(gradAngle, gradFrom, gradTo);
    } else {
      // image — don't clear until URL is entered
    }
  };

  return (
    <div style={stackStyle}>
      <SegmentedControl
        label="Type"
        options={BG_MODE_OPTIONS as unknown as Array<{ value: BgMode; label: string }>}
        value={mode}
        onChange={handleModeChange}
        isDark={isDark}
      />

      {mode === 'solid' && (
        <ColorPicker
          label="Color"
          value={value.backgroundColor}
          onChange={(v) => onChange({ ...value, backgroundColor: v })}
          isDark={isDark}
        />
      )}

      {mode === 'gradient' && (
        <>
          <SliderInput
            label="Angle"
            value={gradAngle}
            onChange={(v) => {
              const a = v ?? 0;
              setGradAngle(a);
              applyGradient(a, gradFrom, gradTo);
            }}
            min={0}
            max={360}
            step={1}
            unit="°"
            isDark={isDark}
          />
          <ColorPicker
            label="From"
            value={gradFrom}
            onChange={(v) => {
              const f = v ?? '#000000';
              setGradFrom(f);
              applyGradient(gradAngle, f, gradTo);
            }}
            isDark={isDark}
          />
          <ColorPicker
            label="To"
            value={gradTo}
            onChange={(v) => {
              const t = v ?? '#ffffff';
              setGradTo(t);
              applyGradient(gradAngle, gradFrom, t);
            }}
            isDark={isDark}
          />
        </>
      )}

      {mode === 'image' && (
        <>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#94a3b8' : '#64748b' }}>
              Image URL
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => {
                  setImageUrl(e.target.value);
                  const url = e.target.value.trim();
                  onChange({
                    ...value,
                    backgroundImage: url ? `url('${escapeCssUrlLiteral(url)}')` : undefined,
                  });
                }}
                placeholder="https://..."
                style={{ ...urlInputStyle, flex: 1 }}
                aria-label="Background image URL"
              />
              {onBrowseAssets && (
                <button
                  type="button"
                  onClick={() => {
                    void onBrowseAssets().then((url) => {
                      if (!url) return;
                      setImageUrl(url);
                      onChange({ ...value, backgroundImage: `url('${escapeCssUrlLiteral(url)}')` });
                    });
                  }}
                  style={{
                    padding: '5px 10px', borderRadius: 4, border: `1px solid ${borderColor}`,
                    background: inputBg, color: textPrimary, fontSize: 12, cursor: 'pointer',
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                >
                  Browse…
                </button>
              )}
            </div>
          </label>

          <SegmentedControl
            label="Size"
            options={BG_SIZE_OPTIONS as unknown as Array<{ value: string; label: string }>}
            value={value.backgroundSize ?? 'cover'}
            onChange={(v) => onChange({ ...value, backgroundSize: v })}
            isDark={isDark}
          />

          <SegmentedControl
            label="Position"
            options={BG_POSITION_OPTIONS as unknown as Array<{ value: string; label: string }>}
            value={value.backgroundPosition ?? 'center'}
            onChange={(v) => onChange({ ...value, backgroundPosition: v })}
            isDark={isDark}
          />

          <SegmentedControl
            label="Repeat"
            options={BG_REPEAT_OPTIONS as unknown as Array<{ value: string; label: string }>}
            value={value.backgroundRepeat ?? 'no-repeat'}
            onChange={(v) => onChange({ ...value, backgroundRepeat: v as StyleProperties['backgroundRepeat'] })}
            isDark={isDark}
          />
        </>
      )}

      <SectionLabel label="Effects" isDark={isDark} />

      <SliderInput
        label="Backdrop Blur"
        value={(() => {
          const v = parseFilterFn(value.backdropFilter, 'blur');
          return v ?? undefined;
        })()}
        onChange={(v) => {
          onChange({
            ...value,
            backdropFilter: v !== undefined ? `blur(${v}px)` : undefined,
          });
        }}
        min={0}
        max={20}
        step={0.5}
        unit="px"
        isDark={isDark}
      />

      <SliderInput
        label="Brightness"
        value={brightness}
        onChange={(v) => {
          setBrightness(v);
          applyFilters(v, grayscale);
        }}
        min={50}
        max={150}
        step={1}
        unit="%"
        isDark={isDark}
      />

      <SliderInput
        label="Grayscale"
        value={grayscale}
        onChange={(v) => {
          setGrayscale(v);
          applyFilters(brightness, v);
        }}
        min={0}
        max={100}
        step={1}
        unit="%"
        isDark={isDark}
      />
    </div>
  );
};

BackgroundTab.displayName = 'BackgroundTab';
